/**
 * Static page app bar (`ExpauseAppBar`) constants.
 *
 * These are opacity / stacking-order values, not sizes — nothing here scales
 * with the viewport — so they live with the other shared UI constants rather
 * than in `responsiveDesign.ts` (`presentation.md` → "Shared constants"). The
 * bar's blur radius, which is a size, lives at `responsive.appBar.backdropBlur`.
 */

export const APP_BAR_CONSTANTS = {
  /**
   * Background fill opacity of the static page app bar (translucent over the
   * scrolling body). Matches the value `FlexibleHeaderList`'s bar settles on
   * when fully collapsed (`0.1 + 0.4 * collapseProgress`) and the project's
   * `${theme.background}80` top-bar convention, so the two app bars read as
   * one surface. Deliberately NOT Flutter's value: Flutter fills its
   * translucent content app bar at `.6` alpha
   * (`content_list_widget.dart:1122-1123`). Opacity is a styling idiom the web
   * may set on its own (`presentation.md` → "Web vs Flutter UI parity scope").
   */
  backgroundOpacity: 0.5,
  /**
   * Stacking band of the sticky page app bar. Deliberately the SAME value
   * `FlexibleHeaderList`'s own bar uses (`FlexibleHeaderList.tsx`), so a user
   * moving between a list page and a static page sees an identical overlay
   * order. Must stay BELOW the sheet host (`SheetService.tsx`, 100) and the
   * fullscreen video overlay (`ContentComponentFullScreenSupport.tsx`, 9999),
   * both of which have to paint over the bar.
   *
   * Follow-up: `FlexibleHeaderList` still hardcodes its own `50` — it is out of
   * this branch's locked scope (the two bars share no code by an explicit
   * story-level scope decision), so the branch that unifies the two bars should
   * collapse that literal onto this token.
   */
  zIndex: 50,
} as const;

export type AppBarConstants = typeof APP_BAR_CONSTANTS;
